import { ComponentProps, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Tooltip } from "react-tooltip";

import { session as sessionApi } from "../../../services/session";
import { PAGE_VIEWS_WARNING_THRESHOLD_PERCENT } from "../../../constants/commonConstants";

/*
 * The quick-look's explanation lives in a tooltip, not in the menu: a paragraph of
 * it under every glance at a dropdown people open often is text read once and then
 * scrolled past forever. Portalled to the document body like the footer's metric
 * tooltips (the menu clips its own overflow), and MODULE-LEVEL so it is one component
 * type across renders, not a new one each time (which would remount the tooltip).
 */
const QuickLookTooltip = (props: ComponentProps<typeof Tooltip>) =>
  createPortal(<Tooltip positionStrategy="fixed" {...props} />, document.body);

/*
 * Unwraps the JSON-RPC envelope - http.ts returns withData(res.data) =
 * { error, data }, where `data` is { jsonrpc, id, result }. Reading res.data
 * yields the ENVELOPE, not the payload.
 */
const rpcResult = (res: any): any =>
  res?.data?.result ?? res?.result ?? res?.data;

/*
 * PAGE VIEWS, ROLLED UP AND BROKEN DOWN.
 *
 * Used in two places - the Settings Account tab and the site switcher's
 * quick-look - so it is one component rather than two implementations that
 * drift.
 *
 * 🔴 THE ALLOWANCE IS ONE POOL. There is no per-site limit, so every share
 * here is a share OF THE ACCOUNT'S ALLOWANCE, never a site's own quota. A
 * per-site bar that filled to 100% would invent a cap that does not exist,
 * and a customer would read "this site is fine" while the account is locked.
 * The bars are deliberately scaled against the limit, so together they read
 * as one allowance being divided up.
 *
 * 🔴 The rows and the total come from ONE call over ONE window
 * (getPageViewsBySite reuses pageViewWindow, the same window the allowance
 * enforces). A breakdown computed over a different period than the total
 * above it is worse than no breakdown - the parts visibly fail to add up.
 */
const PageViewsBreakdown = ({
  userId,
  limit,
  currentSiteId,
  compact = false,
  onlyCurrentSite = false,
}: {
  userId: string;
  limit: number;
  currentSiteId?: string;
  compact?: boolean;
  /*
   * Site tab mode: ONE site's figure, not the whole breakdown.
   *
   * The Site tab is scoped to the selected website, so showing every site
   * there would just duplicate the Account tab one click away - and repeating
   * the same table on two screens is how they drift. Same call, same window,
   * same component; only what is rendered differs.
   */
  onlyCurrentSite?: boolean;
}) => {
  const [data, setData] = useState<any>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const res: any = await sessionApi({
          jsonprc: "2.0",
          method: "getPageViewsBySite",
          params: { userId },
          id: 0,
        } as any);
        const result = rpcResult(res);
        if (!cancelled) {
          if (result?.sites) {
            setData(result);
          } else {
            setFailed(true);
          }
        }
      } catch (e) {
        if (!cancelled) {
          setFailed(true);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [userId]);

  if (failed) {
    /*
     * Says what happened, not what is refused - DESIGN_RULES.md §: an empty
     * state must never describe a refusal. This is a failed read, and saying
     * "no data" instead would be a lie that reads as "you have no traffic".
     */
    return (
      <p className="pv-breakdown-note">
        Could not load the per-site breakdown just now.
      </p>
    );
  }

  if (!data) {
    return <p className="pv-breakdown-note">Loading page views...</p>;
  }

  if (!data.sites.length) {
    return <p className="pv-breakdown-note">No websites yet.</p>;
  }

  /*
   * 🔴 THE SERVER'S LIMIT WINS.
   *
   * The `limit` prop comes from `getEntitlements(authReducer.accountRecord)`,
   * and accountRecord is populated by ActionType.SUBSCRIPTIONDATA - not by
   * LOGIN - so it can be empty after a reload. The RPC computes the same
   * effective limit server-side and always has it. The prop stays as a
   * fallback so an older session still renders something sane.
   *
   * This is what stops the quick-look and the Settings page showing different
   * percentages for the same account.
   */
  const effectiveLimit =
    typeof data.pageViewsLimit === "number" && data.pageViewsLimit > 0
      ? data.pageViewsLimit
      : limit;

  const share = (n: number) =>
    effectiveLimit > 0 ? Math.min((n / effectiveLimit) * 100, 100) : 0;

  /*
   * 🔴 FLOOR, NOT ROUND, FOR THE DISPLAYED PERCENTAGE.
   *
   * `.toFixed(1)` rounds UP, so 9,999 of 10,000 renders as "100.0%" while the
   * escalation state is still "warning" - the customer reads 100% next to an
   * amber bar and reasonably concludes one of them is lying. Likewise 7,999
   * showed "80.0%" in blue, one below the 80% threshold.
   *
   * Flooring means the number never claims more than has actually been used,
   * and it can never announce a threshold the colour has not crossed.
   */
  const showPct = (n: number) => (Math.floor(share(n) * 10) / 10).toFixed(1);

  /*
   * 🔴 SAY THE PERIOD, ALWAYS.
   *
   * These figures are scoped to the BILLING PERIOD, because that is the
   * window the allowance is enforced over. But a bare "0" under a heading
   * that says "Page views" reads as "this website has never had any" - which
   * is what Chris hit on a site with 54 all-time events whose most recent one
   * predates the current period. The number was right and the words were
   * wrong.
   */
  const periodLabel = data.periodStart
    ? new Date(data.periodStart).toLocaleDateString(undefined, {
        day: "numeric",
        month: "short",
      })
    : null;

  const sinceText = periodLabel ? `since ${periodLabel}` : "this billing period";

  if (onlyCurrentSite) {
    const site = data.sites.find((s: any) => s.siteId === currentSiteId);

    if (!site) {
      return (
        <p className="pv-breakdown-note">
          No page views recorded for this website yet.
        </p>
      );
    }

    return (
      <div className="pv-breakdown">
        <div className="pv-breakdown-total">
          <span className="pv-breakdown-total-label">
            This website &middot; {sinceText}
          </span>
          <span className="pv-breakdown-total-value">
            {site.pageViews.toLocaleString()}
          </span>
        </div>

        <div className="pv-breakdown-single">
          <span className="pv-breakdown-bar" aria-hidden="true">
            <span
              className="pv-breakdown-bar-fill"
              style={{ width: `${share(site.pageViews)}%` }}
            />
          </span>
          <span className="pv-breakdown-share">
            {showPct(site.pageViews)}%
          </span>
        </div>

        {/*
          🔴 "Last N days", NEVER "all time".
          Raw data is PURGED at the retention window, so an all-time figure
          would shrink every time the sweep runs and a customer would read
          that as lost traffic. N comes from the same entitlement the purge
          uses, so the number shown and the number deleted cannot describe
          different windows.

          No bar: there is no limit for this to be a share OF. A bar here
          would invent a second allowance.
        */}
        {data.retentionDays && (
          <div className="pv-breakdown-secondary">
            <span className="pv-breakdown-secondary-label">
              Last {data.retentionDays} days
            </span>
            <span className="pv-breakdown-secondary-value">
              {(site.retainedPageViews ?? 0).toLocaleString()}
            </span>
          </div>
        )}

        {/* The account total is the context that makes the figure mean
            something - without it "4,120" answers nothing. */}
        <p className="pv-breakdown-note">
          {site.pageViews === 0 ? (
            (site.retainedPageViews ?? 0) > 0 ? (
              <>
                No page views {sinceText}, but{" "}
                {(site.retainedPageViews ?? 0).toLocaleString()} in the last{" "}
                {data.retentionDays} days - so tracking is working, this
                website has just been quiet since your billing period started.
              </>
            ) : (
              <>
                No page views on this website {sinceText}. Your allowance
                counts from the start of each billing period, so earlier
                traffic is not included here.
              </>
            )
          ) : (
            <>
              {showPct(site.pageViews)}% of your{" "}
              {effectiveLimit > 0 ? effectiveLimit.toLocaleString() : "shared"} page-view
              allowance, shared across all {data.sites.length}{" "}
              {data.sites.length === 1 ? "website" : "websites"} (
              {data.total.toLocaleString()} used in total {sinceText}).
            </>
          )}
        </p>
      </div>
    );
  }

  const totalShare = share(data.total);

  // What the percentages mean, because the obvious reading of a per-row bar is
  // "this site's own limit" - and there isn't one.
  const noteText = `Counted from the start of the current billing period (${sinceText}), the same window your allowance is measured over. The allowance is shared across every website - each share is a portion of that one allowance, not a separate limit.`;

  /*
   * 🔴 ESCALATE COLOUR, NEVER LAYOUT (DESIGN_RULES.md rule 5). The bar never
   * changes size, position or structure as it fills - only its urgency reads
   * differently. Thresholds and colours are the app's own:
   * PAGE_VIEWS_WARNING_THRESHOLD_PERCENT (80) and the $panel-accent-* palette,
   * so this cannot drift from the overage banner that uses the same idea.
   */
  const overallState =
    totalShare >= 100
      ? "is-full"
      : totalShare >= PAGE_VIEWS_WARNING_THRESHOLD_PERCENT
      ? "is-warning"
      : "";

  return (
    <div className={compact ? "pv-breakdown is-compact" : "pv-breakdown"}>
      <div className="pv-breakdown-total">
        <span className="pv-breakdown-total-label">
          Page views &middot; {sinceText}
          {compact && (
            <span
              className="pv-breakdown-info"
              tabIndex={0}
              role="img"
              aria-label={noteText}
            >
              i
            </span>
          )}
        </span>
        <span className="pv-breakdown-total-value">
          {data.total.toLocaleString()}
          {effectiveLimit > 0 && (
            <span className="pv-breakdown-total-limit">
              {" "}/ {effectiveLimit.toLocaleString()}
            </span>
          )}
        </span>
        {/* In the dropdown the percentage sits on the same line as the numbers it
            summarises, right-aligned; the bar beneath then runs the full width. */}
        {compact && effectiveLimit > 0 && (
          <span className={`pv-breakdown-total-share ${overallState}`}>
            {showPct(data.total)}%
          </span>
        )}
      </div>

      {/*
        🔴 THE OVERALL BAR - the one that answers "am I about to hit my
        limit?". The per-site bars below answer a different question ("who is
        using it"), and until now the total was only a number, so the question
        that actually matters had no visual at all.

        It is SEGMENTED by site rather than a single fill: that makes the
        "one shared allowance, divided between your websites" point visually,
        instead of leaving it to a paragraph of explanation underneath.
      */}
      {effectiveLimit > 0 && (
        <div className={`pv-breakdown-overall ${overallState}`}>
          <span className="pv-breakdown-overall-track" aria-hidden="true">
            {data.sites
              .filter((site: any) => site.pageViews > 0)
              .map((site: any, i: number) => (
                <span
                  key={site.siteId}
                  className={
                    site.siteId === currentSiteId
                      ? "pv-breakdown-seg is-current"
                      : "pv-breakdown-seg"
                  }
                  style={{
                    width: `${share(site.pageViews)}%`,
                    // Alternating strength keeps adjacent segments readable
                    // without inventing a per-site colour scale that would
                    // mean nothing.
                    opacity: i % 2 === 0 ? 1 : 0.72,
                  }}
                />
              ))}
          </span>
          {!compact && (
            <span className="pv-breakdown-overall-share">
              {showPct(data.total)}%
            </span>
          )}
        </div>
      )}

      {/*
        🔴 DIVS, NOT ul/li.
        This component renders inside the site-switcher menu, and
        `.header .site-selector-list li` is a DESCENDANT selector - it matched
        these rows too, imposing display:flex, a #f5f5f5 pill, 7px/10px
        padding and a blue hover on each one. The grid collapsed and the
        result was unreadable.
        Using divs removes the failure mode rather than fighting it with
        specificity, and these rows were never a navigable list anyway.
      */}
      <div className="pv-breakdown-list">
        {data.sites.map((site: any) => (
          <div
            key={site.siteId}
            className={
              site.siteId === currentSiteId
                ? "pv-breakdown-row is-current"
                : "pv-breakdown-row"
            }
          >
            <span className="pv-breakdown-host" title={site.host}>
              {site.host}
            </span>
            <span className="pv-breakdown-count">
              {site.pageViews.toLocaleString()}
            </span>
            <span className="pv-breakdown-bar" aria-hidden="true">
              <span
                className="pv-breakdown-bar-fill"
                style={{ width: `${share(site.pageViews)}%` }}
              />
            </span>
            <span className="pv-breakdown-share">
              {showPct(site.pageViews)}%
            </span>
          </div>
        ))}
      </div>

      {/* Says plainly what the percentages mean (see noteText). In the dropdown it
          is a tooltip on the (i) beside the heading; on the Settings page there
          is room, so it stays as text. */}
      {compact ? (
        <QuickLookTooltip
          anchorSelect=".pv-breakdown-info"
          className="visitor-category-tooltip"
          place="bottom"
        >
          {noteText}
        </QuickLookTooltip>
      ) : (
        <p className="pv-breakdown-note">{noteText}</p>
      )}
    </div>
  );
};

export default PageViewsBreakdown;
